/**
 * Public /security marketing surface.
 *
 * Mirrors the security section on /about (which is auth-gated)
 * verbatim, so prospects can read it without signing up. Both pages
 * read from the same `/api/page-content/about` endpoint — editing
 * via the CMS updates both surfaces in one shot, no duplication.
 *
 * Public route — does NOT live under the ProtectedRoute wrapper.
 * Renders its own minimal Forge Ledger header/footer (we can't reuse
 * the authed Layout because that triggers a redirect to /login when
 * not signed in).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { RenderMarkdownInline } from '../lib/markdown';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';

const Security = () => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/page-content/about`)
      .then((r) => setContent(r.data))
      .catch(() => setContent({}));  // soft-fail to defaults rendered inline
  }, []);

  const pillars = content?.security_pillars || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="security-page">
      <Seo
        title="Forge Ledger — Your data, kept safe"
        description="How Forge Ledger protects your money data: bcrypt passwords, TLS 1.3, encrypted-at-rest MongoDB Atlas, Stripe-isolated card details, full UK GDPR right of access + erasure."
        path="/security"
      />
      {/* Lightweight public header — no nav, just brand + back link */}
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="security-brand-link">
            <img src={LOGO_URL} alt="Forge Ledger" className="w-7 h-7 rounded-md object-cover ring-1 ring-[#E2E8F0]" />
            <span className="font-extrabold text-[#020617] tracking-tight">Forge Ledger</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-[#0052FF] hover:underline inline-flex items-center gap-1"
            data-testid="security-login-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-12 sm:py-16 flex-1 w-full">
        {/* Hero */}
        <div className="flex items-start gap-4 mb-10">
          <div className="w-12 h-12 rounded-xl bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-7 h-7 text-[#0052FF]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0052FF] mb-2">Security & data</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#020617] tracking-tighter leading-tight">
              {content?.security_heading || 'Your data, kept safe'}
            </h1>
          </div>
        </div>

        {/* Intro */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 mb-6">
          <p className="text-base text-[#475569] leading-relaxed">
            <RenderMarkdownInline text={content?.security_intro || 'Forge Ledger holds the future of your finances — we take that responsibility seriously.'} />
          </p>
        </div>

        {/* Pillars */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 space-y-5" data-testid="security-pillars">
          {pillars.length === 0 ? (
            <p className="text-sm text-[#94A3B8] italic">Loading security details…</p>
          ) : (
            pillars.map((pillar, i) => (
              <div
                key={i}
                className="border-l-2 border-[#DBEAFE] pl-4"
                data-testid={`security-pillar-${i}`}
              >
                <h2 className="font-bold text-[#020617] text-[15px] mb-1.5">{pillar.heading}</h2>
                <p className="text-sm text-[#475569] leading-relaxed">
                  <RenderMarkdownInline text={pillar.body} />
                </p>
              </div>
            ))
          )}
        </div>

        {/* Footnote — responsible disclosure email */}
        {content?.security_footnote && (
          <p className="text-sm text-[#64748B] italic mt-6 px-2">
            <RenderMarkdownInline text={content.security_footnote} />
          </p>
        )}

        {/* CTA strip */}
        <div className="mt-12 bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 text-center">
          <p className="text-base text-[#020617] font-semibold mb-1">
            Ready to forge yours?
          </p>
          <p className="text-sm text-[#64748B] mb-4">
            Free forever on the Ember tier — no card required.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#0052FF] text-white font-semibold text-sm hover:bg-[#003ECC] transition-colors"
              data-testid="security-cta-register"
            >
              Create an account
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#F1F5F9] text-[#020617] font-semibold text-sm hover:bg-[#E2E8F0] transition-colors"
              data-testid="security-cta-login"
            >
              Sign in
            </Link>
          </div>
        </div>
      </main>

      <Footer variant="full" />
    </div>
  );
};

export default Security;
